import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { resolveDayHours, SpecialHours } from '../shared/availability';

/**
 * SCHEDULE GLOBALIZATION (2026-08-01) — the ONE public-readable source of shop hours.
 *
 * Mirror of customer-portal's publicHoursService. The owner sets hours in POS Settings,
 * which writes settings/shop (STAFF-ONLY) and mirrors businessHours to publicConfig/hours
 * (PUBLIC-READ). The public guest booking app reads hours through THIS service so it reflects
 * the same owner-set schedule as the customer portal and the in-house calendar.
 *
 * BEHAVIOR-PIN: SCHEDULE-GLOBALIZATION-2026-08-01.md
 */

export interface DayHours {
  open: string;    // 'HH:MM' 24h
  close: string;   // 'HH:MM' 24h
  closed: boolean;
}

type BusinessHours = { [dayName: string]: DayHours };

// Sane floor if the mirror doc is momentarily missing. NOT the source of truth.
const FALLBACK_HOURS: BusinessHours = {
  sunday: { open: '10:00', close: '16:00', closed: true },
  monday: { open: '09:00', close: '20:00', closed: false },
  tuesday: { open: '09:00', close: '20:00', closed: false },
  wednesday: { open: '09:00', close: '20:00', closed: false },
  thursday: { open: '09:00', close: '20:00', closed: false },
  friday: { open: '09:00', close: '20:00', closed: false },
  saturday: { open: '09:00', close: '20:00', closed: false },
};

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

class PublicHoursService {
  private cache: BusinessHours | null = null;
  private specialCache: SpecialHours = {}; // holiday / special-date overrides (publicConfig/hours.specialHours)
  private cacheAt = 0;
  private readonly TTL_MS = 60_000;

  getDayName(date: Date): string {
    const stable = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
    return DAY_NAMES[stable.getDay()];
  }

  async getBusinessHours(): Promise<BusinessHours> {
    const now = Date.now();
    if (this.cache && now - this.cacheAt < this.TTL_MS) {
      return this.cache;
    }
    try {
      const snap = await getDoc(doc(db, 'publicConfig', 'hours'));
      if (snap.exists() && snap.data()?.businessHours) {
        this.cache = snap.data()!.businessHours as BusinessHours;
        this.specialCache = (snap.data()!.specialHours as SpecialHours) || {}; // holiday closures / special hours
        this.cacheAt = now;
        return this.cache;
      }
      console.warn('⚠️ publicConfig/hours missing — using fallback hours');
    } catch (err) {
      console.warn('⚠️ Could not read public hours, using fallback:', err);
    }
    this.cache = FALLBACK_HOURS;
    this.cacheAt = now;
    return FALLBACK_HOURS;
  }

  async getDayHours(date: Date): Promise<DayHours> {
    const hours = await this.getBusinessHours();
    const day = this.getDayName(date);
    const weekly = hours[day] || FALLBACK_HOURS[day];
    const r = resolveDayHours(date, weekly, this.specialCache); // holiday / special-date override wins
    return { closed: !!r?.closed, open: r?.open || weekly.open, close: r?.close || weekly.close };
  }

  /** SYNC closed check — honors weekly + one-off/holiday closures (Jose 2026-11-02, client closed-day banner).
   *  Call AFTER getBusinessHours() has warmed the cache. Cold cache → false (never falsely close the shop). */
  isClosedOn(date: Date): boolean {
    const weekly = (this.cache as any)?.[this.getDayName(date)];
    if (!weekly) return false;
    const r = resolveDayHours(date, weekly, this.specialCache);
    return !!(r as any)?.closed;
  }

  /** Owner's note for a one-off closed/special date (e.g. "Snowstorm ❄️"), if any. */
  specialNote(date: Date): string {
    const k = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return (this.specialCache as any)?.[k]?.note || '';
  }

  /** 'HH:MM' 24h → '9:00 AM' style. */
  private to12h(hhmm: string): string {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}:${String(m || 0).padStart(2, '0')} ${period}`;
  }

  /**
   * Owner-set hours grouped into readable footer lines, e.g.
   * [{label:'Monday – Friday', value:'9:00 AM – 8:00 PM'}, {label:'Sunday', value:'Closed'}].
   * Consecutive days with identical hours collapse into one range (Mon-first display order).
   */
  async getFormattedHours(): Promise<{ label: string; value: string }[]> {
    const hours = await this.getBusinessHours();
    const ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const LABEL: Record<string, string> = {
      monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
      friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
    };
    const sig = (d: DayHours) => (d.closed ? 'Closed' : `${this.to12h(d.open)} – ${this.to12h(d.close)}`);
    const out: { label: string; value: string }[] = [];
    let i = 0;
    while (i < ORDER.length) {
      const value = sig(hours[ORDER[i]] || FALLBACK_HOURS[ORDER[i]]);
      let j = i;
      while (j + 1 < ORDER.length && sig(hours[ORDER[j + 1]] || FALLBACK_HOURS[ORDER[j + 1]]) === value) j++;
      const label = i === j ? LABEL[ORDER[i]] : `${LABEL[ORDER[i]]} – ${LABEL[ORDER[j]]}`;
      out.push({ label, value });
      i = j + 1;
    }
    return out;
  }

  invalidate(): void {
    this.cache = null;
    this.cacheAt = 0;
  }
}

export const publicHoursService = new PublicHoursService();
